import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle, Users, ShieldAlert, RefreshCw } from "lucide-react";
import {
  recoveryStatePresentation,
  recoveryReasonDetailExplanation,
} from "../../lib/emailHygieneRecoveryState";
import {
  familyEvidencePresentation,
  familyLabel,
  identityRelationshipLabel,
  recordSafetyShapePresentation,
} from "../../lib/emailHygieneVerificationEvidence";

/**
 * EmailHygieneReconciliationDetail — DASH-EMAIL-013.
 *
 * Presentation-only: every fact rendered here is read directly off the
 * real RECON-002/003 ReconciliationDetailResponse. This component makes
 * no decisions of its own — no state-transition logic, no re-derivation
 * of which evidence "wins", no reclassification. `onSelectAction` is
 * invoked with the exact backend-returned `available_actions[]` entry the
 * operator picked; this component never invents or infers an action from
 * `reconciliation_reason`.
 *
 * Provider-specific evidence is rendered via a generic key/value renderer
 * with human-readable labels for known SendGrid keys and a safe fallback
 * for anything unrecognized — this component is not a
 * `provider === "sendgrid"` branch, so an unknown/future provider string
 * (e.g. a future SES adapter) renders without crashing or requiring a
 * SendGrid-shaped detail model.
 */

// Known SendGrid provider_specific_evidence keys -> human labels. Purely a
// display nicety — any key NOT in this map still renders, humanized via
// KNOWN_FIELD_FALLBACK_LABEL, so a future provider's own fields are never
// silently dropped.
const KNOWN_PROVIDER_FIELD_LABELS = {
  bounce_classification: "Bounce Classification",
  smtp_status: "SMTP Status",
  reason: "Reason",
  sending_ip: "Sending IP",
  asm_group_id: "ASM Group ID",
  sendgrid_event_id: "SendGrid Event ID",
  sendgrid_message_id: "SendGrid Message ID",
};

function humanizeFieldKey(key) {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex flex-col" data-testid={testId}>
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm text-slate-100">{value ?? <span className="text-slate-500">Unavailable</span>}</span>
    </div>
  );
}

function KeyValueGrid({ entries, emptyLabel }) {
  if (!entries.length) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col">
          <dt className="text-xs text-slate-400">{KNOWN_PROVIDER_FIELD_LABELS[key] || humanizeFieldKey(key)}</dt>
          <dd className="break-words text-sm text-slate-100">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * ProviderVerificationEvidenceCard — EMAIL-HYGIENE-RECOVERY-EVIDENCE-001 (Gate D).
 *
 * Renders the backend-derived, READ-ONLY decomposition of the latest
 * persisted provider verification. Every value shown is read straight off
 * `detail.provider_verification`; this component derives nothing.
 *
 * Deliberately renders NO action control. A verification is an observation,
 * and Gate D is visibility only — no Retry, Resolve, Reproject or Converge
 * control belongs here.
 */
function ProviderVerificationEvidenceCard({ verification, currentEvidence, candidateEvidence, monotonicity }) {
  if (!verification) return null;
  const shape = recordSafetyShapePresentation(verification.record_safety_shape);
  const identity = identityRelationshipLabel(verification.identity_relationship);

  return (
    <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-verification-evidence">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-slate-100">
          <ShieldAlert className="h-4 w-4 text-amber-400" />
          Provider Verification Evidence
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-400">
          A read-only check of what the CRM currently holds. It changes nothing, and the reconciliation
          stays open regardless of the result.
        </p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryRow
            label="Outcome"
            value={verification.outcome}
            testId="email-hygiene-verification-outcome"
          />
          <SummaryRow
            label="Verified"
            value={verification.verified_at ? new Date(verification.verified_at).toLocaleString() : null}
          />
          <SummaryRow label="Attempt" value={verification.attempt_number ?? null} />
          <SummaryRow
            label="Contacts (was / now)"
            value={
              verification.current_matched_contact_count === null &&
              verification.historical_matched_contact_count === null
                ? null
                : `${verification.historical_matched_contact_count ?? "—"} / ${verification.current_matched_contact_count ?? "—"}`
            }
          />
        </div>

        {identity && (
          <SummaryRow
            label="Contact identity"
            value={identity}
            testId="email-hygiene-verification-identity-relationship"
          />
        )}

        {shape && (
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="email-hygiene-verification-safety-shape">
            <Badge className={`${shape.className} text-xs`}>{shape.label}</Badge>
            <p className="mt-2 text-xs text-slate-400">{shape.explanation}</p>
          </div>
        )}

        {verification.contact_results?.length > 0 ? (
          <div className="space-y-2" data-testid="email-hygiene-verification-families">
            {verification.contact_results.map((result, idx) => (
              <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <div className="text-xs text-slate-400">
                  Contact {idx + 1} of {verification.contact_results.length} — verdict:{" "}
                  <span className="text-slate-200">{result.verdict}</span>
                </div>
                <div className="mt-2 space-y-2">
                  {Object.entries(result.derived_field_family_evidence || {}).map(([family, value]) => {
                    const presentation = familyEvidencePresentation(value);
                    return (
                      <div key={family} className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-slate-200">{familyLabel(family)}</span>
                          {presentation ? (
                            <Badge className={`${presentation.className} text-xs`}>{presentation.label}</Badge>
                          ) : (
                            <Badge className="bg-slate-500/20 text-slate-400 text-xs">{value}</Badge>
                          )}
                        </div>
                        {presentation && <p className="text-xs text-slate-400">{presentation.explanation}</p>}
                      </div>
                    );
                  })}
                </div>
                {(result.observed_validation_timestamp || result.observed_delivery_timestamp) && (
                  <div className="mt-2 text-xs text-slate-500">
                    Observed CRM dates —{" "}
                    {`validation: ${
                      result.observed_validation_timestamp
                        ? new Date(result.observed_validation_timestamp).toLocaleString()
                        : "none"
                    }, delivery: ${
                      result.observed_delivery_timestamp
                        ? new Date(result.observed_delivery_timestamp).toLocaleString()
                        : "none"
                    }`}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            No per-contact comparison was recorded for this verification.
          </p>
        )}

        {(verification.failure_category || verification.failure_detail_safe) && (
          <div className="rounded-md border border-rose-500/20 bg-rose-500/5 p-3 text-xs text-rose-200">
            {verification.failure_category && <div>Failure: {verification.failure_category}</div>}
            {verification.failure_detail_safe && <div className="mt-1">{verification.failure_detail_safe}</div>}
          </div>
        )}

        <div className="border-t border-white/10 pt-3">
          <div className="text-xs text-slate-400">CRM configuration fingerprint</div>
          <div className="break-all font-mono text-xs text-slate-300" data-testid="email-hygiene-verification-fingerprint">
            {verification.ghl_field_ids_fingerprint || "—"}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Identifies which governed field configuration produced this observation. It does not establish
            which field configuration was in use when the original projection ran.
          </p>
        </div>

        <div className="border-t border-white/10 pt-3">
          <div className="text-xs text-slate-400">Local evidence watermark</div>
          <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SummaryRow
              label="Current committed"
              value={
                currentEvidence?.event_timestamp
                  ? new Date(currentEvidence.event_timestamp).toLocaleString()
                  : null
              }
              testId="email-hygiene-verification-current-watermark"
            />
            <SummaryRow
              label="This candidate"
              value={
                candidateEvidence?.event_timestamp
                  ? new Date(candidateEvidence.event_timestamp).toLocaleString()
                  : null
              }
              testId="email-hygiene-verification-candidate-watermark"
            />
          </div>
          {monotonicity && (
            <p className="mt-2 text-xs text-slate-400" data-testid="email-hygiene-verification-monotonicity">
              Local policy disposition if evaluated now:{" "}
              <span className="font-mono text-slate-300">{monotonicity}</span>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ResolutionBanner({ resolution }) {
  if (!resolution) return null;

  const failedComponents = (resolution.finalization?.components || []).filter((c) => !c.succeeded);

  if (resolution.resolved && resolution.finalization?.fully_finalized) {
    return (
      <Card className="glass border-emerald-500/20" data-testid="email-hygiene-reconciliation-resolution-resolved">
        <CardContent className="flex items-start gap-3 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
          <div>
            <p className="font-medium text-emerald-300">Resolved</p>
            <p className="mt-1 text-sm text-slate-300">
              Business reconciliation and secondary bookkeeping both completed
              {resolution.resolved_at ? ` on ${new Date(resolution.resolved_at).toLocaleString()}` : ""}
              {resolution.resolved_by ? ` by ${resolution.resolved_by}` : ""}.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (resolution.resolved && !resolution.finalization?.fully_finalized) {
    return (
      <Card className="glass border-amber-500/20" data-testid="email-hygiene-reconciliation-resolution-incomplete">
        <CardContent className="flex items-start gap-3 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <div>
            <p className="font-medium text-amber-300">Resolved — Administrative Finalization Required</p>
            <p className="mt-1 text-sm text-slate-300">
              The business mutation already succeeded — this action will not be offered again. The following
              secondary bookkeeping step(s) did not complete and may need operator attention:
            </p>
            <ul className="mt-1 list-inside list-disc text-sm text-slate-300">
              {failedComponents.map((c) => (
                <li key={c.component}>{humanizeFieldKey(c.component)}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-resolution-unresolved">
      <CardContent className="flex items-start gap-3 p-4">
        <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <div>
          <p className="font-medium text-slate-200">Attempted — Still Unresolved</p>
          <p className="mt-1 text-sm text-slate-300">
            A previous attempt ({resolution.action_type}) did not resolve this reconciliation. It remains active
            and reviewable.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * PropagationReconciliationDetail — EMAIL-HYGIENE-GATE-3A.
 *
 * Read-only detail for a cross-environment propagation NEEDS_ATTENTION
 * record (`record_type === "propagation"`). Deliberately renders no
 * action controls (there is no action-executor for propagation records —
 * see EmailHygieneReconciliationView.jsx's own handleSelectAction, which
 * is never wired up here) and fabricates no native reconciliation
 * lifecycle/finalization concept — every fact here is read directly off
 * `detail.propagation` (the real, durable PropagationRecord evidence).
 * `detail.summary` is the backend's own plain-language framing
 * ("Qualification ingestion succeeded, this is a Production
 * synchronization failure") so this component does not need to
 * re-derive that distinction itself.
 */
function PropagationReconciliationDetail({ detail, onBack }) {
  const propagation = detail.propagation || {};
  const targets = propagation.targets || [];

  return (
    <div className="space-y-4" data-testid="email-hygiene-reconciliation-detail">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 text-slate-300 hover:text-slate-100"
        onClick={onBack}
        data-testid="email-hygiene-reconciliation-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Reconciliation Queue
      </Button>

      <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-summary">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-400" />
              <span className="text-lg font-semibold text-slate-100">{detail.recipient}</span>
              <Badge className="bg-amber-500/20 text-amber-300 text-xs">Production Sync</Badge>
              <Badge className="bg-slate-500/20 text-slate-300 text-xs capitalize">{detail.source_environment}</Badge>
            </div>
            <span className="text-xs text-slate-500" data-testid="email-hygiene-reconciliation-no-action">
              No action currently available — read-only
            </span>
          </div>

          <p className="mt-3 text-sm text-slate-300" data-testid="email-hygiene-reconciliation-propagation-summary">
            {detail.summary}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SummaryRow label="Canonical Classification" value={propagation.canonical_classification} />
            <SummaryRow label="Canonical Hygiene State" value={propagation.canonical_hygiene_state} />
            <SummaryRow label="Canonical Production Eligibility" value={propagation.canonical_production_eligibility} />
            <SummaryRow
              label="Canonical Evidence Timestamp"
              value={propagation.canonical_evidence_timestamp ? new Date(propagation.canonical_evidence_timestamp).toLocaleString() : null}
            />
            <SummaryRow label="Source Event" value={propagation.source_event_identity} />
            <SummaryRow label="Source Environment" value={propagation.source_environment} testId="email-hygiene-reconciliation-summary-source-environment" />
            <SummaryRow label="Aggregate Status" value={propagation.aggregate_status} />
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-propagation-targets">
        <CardHeader>
          <CardTitle className="text-base text-slate-100">Target Environments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {targets.map((target, idx) => (
            <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-sm" data-testid={`email-hygiene-reconciliation-propagation-target-${target.environment}`}>
              <div className="flex flex-wrap items-center gap-2 text-slate-200">
                <span className="capitalize font-medium">{target.environment}</span>
                <Badge className="bg-amber-500/20 text-amber-300 text-xs">{target.status}</Badge>
                {target.needs_attention && <Badge className="bg-red-500/20 text-red-300 text-xs">Needs Attention</Badge>}
                {target.automatic_retry_enabled && <Badge className="bg-indigo-500/20 text-indigo-300 text-xs">Automatic Retry Enabled</Badge>}
                {target.mutation_uncertain && <Badge className="bg-amber-500/20 text-amber-300 text-xs">Mutation Uncertain</Badge>}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <SummaryRow label="Attempts" value={target.attempts} />
                <SummaryRow label="Last Error Category" value={target.last_error_category} />
                <SummaryRow
                  label="Last Attempted"
                  value={target.last_attempted_at ? new Date(target.last_attempted_at).toLocaleString() : null}
                />
                <SummaryRow
                  label="Resolved At"
                  value={target.resolved_at ? new Date(target.resolved_at).toLocaleString() : null}
                />
              </div>
              <div className="mt-2 text-xs text-slate-400">
                Matched contacts: {target.matched_contact_ids?.length ? target.matched_contact_ids.join(", ") : "None"}
              </div>
              <div className="text-xs text-slate-400">
                Updated contacts: {target.updated_contact_ids?.length ? target.updated_contact_ids.join(", ") : "None"}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default function EmailHygieneReconciliationDetail({ detail, canExecute, onBack, onSelectAction }) {
  if (!detail) return null;

  if (detail.record_type === "propagation") {
    return <PropagationReconciliationDetail detail={detail} onBack={onBack} />;
  }

  const decision = detail.missionassist_decision || {};
  const commonEvidence = detail.common_evidence || {};
  const providerSpecific = detail.provider_specific_evidence || {};
  const crmProjection = detail.crm_projection || { matched_contact_count: 0, matched_contact_ids: [], results: [] };
  const actions = detail.available_actions || [];
  const providerFieldEntries = Object.entries(providerSpecific);
  // EMAIL-HYGIENE-RECOVERY-STATE-001 (Phase 1): backend-derived. The frontend
  // never decides what happens next, and never decides what may be done --
  // `actions` remains the sole source of controls, unchanged.
  const recovery = recoveryStatePresentation(detail.recovery_state);
  // EMAIL-HYGIENE-PROVIDER-VERIFY-001 (Phase 2a): a structured finding for the
  // verification results that share `operator_review_required`.
  const recoveryDetailCopy = recoveryReasonDetailExplanation(detail.recovery_reason_detail);

  return (
    <div className="space-y-4" data-testid="email-hygiene-reconciliation-detail">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 text-slate-300 hover:text-slate-100"
        onClick={onBack}
        data-testid="email-hygiene-reconciliation-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Reconciliation Queue
      </Button>

      {/* Header / operator summary */}
      <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-summary">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-indigo-400" />
              <span className="text-lg font-semibold text-slate-100">{detail.recipient}</span>
              <Badge className="bg-indigo-500/20 text-indigo-300 text-xs capitalize">{detail.provider}</Badge>
              <Badge className="bg-slate-500/20 text-slate-300 text-xs capitalize">{detail.environment}</Badge>
            </div>
            {actions.length > 0 ? (
              <div className="flex flex-col items-end gap-1" data-testid="email-hygiene-reconciliation-actions">
                {recovery && (
                  <Badge className={`${recovery.className} text-xs`}>{recovery.label}</Badge>
                )}
                {(recovery?.explanation || recoveryDetailCopy) && (
                  <span
                    className="max-w-sm text-right text-xs text-slate-400"
                    data-testid="email-hygiene-reconciliation-recovery-explanation"
                  >
                    {recoveryDetailCopy || recovery.explanation}
                  </span>
                )}
                <div className="flex flex-wrap gap-2">
                {canExecute ? (
                  actions.map((action) => (
                    <Button
                      key={action.action_type}
                      type="button"
                      size="sm"
                      onClick={() => onSelectAction?.(action)}
                      data-testid={`email-hygiene-reconciliation-action-button-${action.action_type}`}
                    >
                      {action.label}
                    </Button>
                  ))
                ) : (
                  <span className="text-xs text-slate-500" data-testid="email-hygiene-reconciliation-action-readonly">
                    Read-only — admin access required to act on this reconciliation
                  </span>
                )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-end gap-1" data-testid="email-hygiene-reconciliation-no-action">
                {recovery ? (
                  <>
                    <Badge className={`${recovery.className} text-xs`}>{recovery.label}</Badge>
                    {recovery.explanation && (
                      <span
                        className="max-w-sm text-right text-xs text-slate-400"
                        data-testid="email-hygiene-reconciliation-recovery-explanation"
                      >
                        {recovery.explanation}
                      </span>
                    )}
                    {recoveryDetailCopy && (
                      <span
                        className="max-w-sm text-right text-xs text-slate-400"
                        data-testid="email-hygiene-reconciliation-recovery-detail-explanation"
                      >
                        {recoveryDetailCopy}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-slate-500">No action currently available</span>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SummaryRow label="Classification" value={decision.classification} testId="email-hygiene-reconciliation-summary-classification" />
            <SummaryRow label="Current Hygiene State" value={decision.current_hygiene_state} />
            <SummaryRow label="Candidate Hygiene State" value={decision.candidate_hygiene_state} testId="email-hygiene-reconciliation-summary-candidate-state" />
            <SummaryRow label="Candidate Eligibility" value={decision.candidate_production_eligibility} />
            <SummaryRow
              label="Reconciliation Reason"
              value={detail.reconciliation_reason}
              testId="email-hygiene-reconciliation-summary-reason"
            />
            <SummaryRow
              label="Recovery Status"
              value={recovery?.label || detail.recovery_state}
              testId="email-hygiene-reconciliation-summary-recovery-state"
            />
            {detail.last_verified_at && (
              <SummaryRow
                label="Provider State Last Verified"
                value={new Date(detail.last_verified_at).toLocaleString()}
                testId="email-hygiene-reconciliation-summary-last-verified"
              />
            )}
            <SummaryRow
              label="Matched CRM Contacts"
              value={crmProjection.matched_contact_count}
              testId="email-hygiene-reconciliation-summary-matched"
            />
            <SummaryRow
              label="Provider Mutation Risk"
              value={detail.provider_mutation_may_have_occurred ? "Yes" : "No"}
            />
            <SummaryRow
              label="Entered Reconciliation"
              value={detail.entered_reconciliation_at ? new Date(detail.entered_reconciliation_at).toLocaleString() : null}
            />
          </div>
        </CardContent>
      </Card>

      <ResolutionBanner resolution={detail.resolution} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Common provider evidence */}
        <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-common-evidence">
          <CardHeader>
            <CardTitle className="text-base text-slate-100">Common Provider Evidence</CardTitle>
          </CardHeader>
          <CardContent>
            {commonEvidence.event_found ? (
              <KeyValueGrid
                entries={[
                  ["provider", commonEvidence.provider],
                  ["event_type", commonEvidence.event_type],
                  ["event_timestamp", commonEvidence.event_timestamp ? new Date(commonEvidence.event_timestamp).toLocaleString() : "—"],
                  ["event_identity", commonEvidence.event_identity],
                  ["message_identity", commonEvidence.message_identity || "—"],
                ]}
                emptyLabel="No common evidence available."
              />
            ) : (
              <p className="text-sm text-slate-500">
                The underlying provider event evidence could not be located for this reconciliation.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Provider-specific evidence */}
        <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-provider-evidence">
          <CardHeader>
            <CardTitle className="text-base text-slate-100">Provider-Specific Evidence</CardTitle>
          </CardHeader>
          <CardContent>
            <KeyValueGrid entries={providerFieldEntries} emptyLabel="No provider-specific evidence returned." />
          </CardContent>
        </Card>
      </div>

      {/* EMAIL-HYGIENE-RECOVERY-EVIDENCE-001 (Gate D): renders only when a
          verification has actually been performed. A reconciliation that was
          never verified renders nothing here rather than an empty shell. */}
      <ProviderVerificationEvidenceCard
        verification={detail.provider_verification}
        currentEvidence={detail.current_effective_evidence}
        candidateEvidence={detail.candidate_effective_evidence}
        monotonicity={detail.local_monotonicity_disposition}
      />

      {/* CRM / GHL projection */}
      <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-crm-projection">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-slate-100">
            <Users className="h-4 w-4 text-indigo-400" />
            CRM Projection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-300">
            Matched contacts:{" "}
            <span className="font-mono text-slate-100" data-testid="email-hygiene-reconciliation-crm-matched-count">
              {crmProjection.matched_contact_count}
            </span>
          </p>
          {crmProjection.matched_contact_ids?.length > 0 && (
            <p className="break-words text-xs text-slate-400">{crmProjection.matched_contact_ids.join(", ")}</p>
          )}
          {crmProjection.results?.length > 0 ? (
            <div className="space-y-2">
              {crmProjection.results.map((result, idx) => (
                <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-slate-200">
                    <span className="capitalize">{result.environment}</span>
                    {result.reconciliation_reason && (
                      <Badge className="bg-amber-500/20 text-amber-300 text-xs">{result.reconciliation_reason}</Badge>
                    )}
                    {result.reconciliation_lifecycle && (
                      <Badge className="bg-slate-500/20 text-slate-300 text-xs">{result.reconciliation_lifecycle}</Badge>
                    )}
                    {result.retryable && <Badge className="bg-indigo-500/20 text-indigo-300 text-xs">Retryable</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Updated contacts: {result.updated_contact_ids?.length ? result.updated_contact_ids.join(", ") : "None"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No CRM projection results recorded.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
